import { useState, useEffect } from 'react';

import { getClientProducts } from 'api/generated/client-products/client-products';
import type { ClientProductDto } from 'api/generated/schemas';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type ClientProduct = Omit<ClientProductDto, 'type'> & { type: string };

interface UseClientProductsResult {
  clientProducts: ClientProduct[];
  loading: boolean;
  error: unknown;
}

const useClientProducts = (): UseClientProductsResult => {
  const [clientProducts, setClientProducts] = useState<ClientProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const currentUser = useCurrentUser();

  useEffect(() => {
    const fetchClientProducts = async (): Promise<void> => {
      if (!currentUser?.data?.client?.id || !currentUser?.data?.oktaUserId)
        return;

      setLoading(true);
      setError(null);

      try {
        const list = await getClientProducts(
          currentUser.data.oktaUserId,
          Number(currentUser.data.client.id),
          { size: 1000 },
        );

        if (list) {
          setClientProducts(
            list.map((product) => ({
              ...product,
              type: String(product.type ?? ''),
            })),
          );
        }
      } catch (err) {
        console.error('Error fetching client products:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientProducts();
  }, [currentUser?.data?.client?.id, currentUser?.data?.oktaUserId]);

  return {
    clientProducts,
    loading,
    error,
  };
};

export default useClientProducts;
